import { GeneralTooltip } from '@components/common/GeneralTooltip';
import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import type React from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  currentIndex: number;
  totalConversations: number;
  hasMultilingualTrainAI: boolean;
  isTranslating: boolean;
  isDisabled: boolean;
  isEvaluable: boolean;
  hasTranslation: boolean;
  translateLabel: string;
  onTranslate: () => void;
  onSkip: () => void;
};

export const ConversationReviewCardHeader: React.FC<Props> = ({
  currentIndex,
  totalConversations,
  hasMultilingualTrainAI,
  isTranslating,
  isDisabled,
  isEvaluable,
  hasTranslation,
  translateLabel,
  onTranslate,
  onSkip,
}) => {
  const { t } = useTranslation();
  const isLastConversation = currentIndex === totalConversations - 1;
  // Show the translate button in the card header only before translation is available.
  // Once a translation exists the toggle moves to the question section header instead.
  const showTranslateButton = hasMultilingualTrainAI && !hasTranslation;

  return (
    <div className='conversation-review-modal__card-header'>
      <span className='conversation-review-modal__card-header-title'>
        {t('train-ai.conversation-review.question-prefix')} {currentIndex + 1}{' '}
        {t('train-ai.conversation-review.of-total', {
          total: totalConversations,
        })}
      </span>
      {!isEvaluable && (
        <GeneralTooltip
          text={t('train-ai.conversation-review.already-evaluated.tooltip')}
          placement='bottom'
          // mr-auto/ml-2 belong on the wrapper: GeneralTooltip renders a div around the
          // badge, so the wrapper - not the badge - is the flex child of the card header.
          parentClassName='flex mr-auto ml-2'
          // The base tooltip is w-max and centred on its trigger. The badge sits near the
          // card's left edge and the card clips its overflow, so a single-line tooltip would
          // be cut off - pin it to the left edge and let it wrap instead.
          className='!left-0 right-auto !-translate-x-0 max-w-[150px] whitespace-normal text-left'>
          <span className='conversation-review-modal__already-evaluated'>
            <MaterialIcon
              icon='warning'
              classes={['text-tiny', 'sm:text-sm', 'leading-none']}
            />
            {t('train-ai.conversation-checklist.table.not-evaluable')}
          </span>
        </GeneralTooltip>
      )}
      <div className='conversation-review-modal__card-header-actions'>
        {showTranslateButton && (
          <ButtonT
            type='button'
            styling='outline'
            size='tiny'
            rounding='md'
            disabled={isTranslating}
            onClick={onTranslate}
            className='conversation-review-modal__card-header-button'>
            <MaterialIcon
              icon='translate'
              classes={['conversation-review-modal__card-header-button-icon']}
            />
            {isTranslating
              ? t('train-ai.conversation-review.language.translating')
              : translateLabel}
          </ButtonT>
        )}
        <ButtonT
          type='button'
          styling='outline'
          size='tiny'
          rounding='md'
          disabled={isLastConversation || isDisabled}
          onClick={onSkip}
          className='conversation-review-modal__card-header-button'>
          <MaterialIcon
            icon='skip_next'
            classes={['conversation-review-modal__card-header-button-icon']}
          />
          {t('train-ai.conversation-review.action.skip')}
        </ButtonT>
      </div>
    </div>
  );
};
